/**
 * 学习乐园 · 数织 Nonogram（game-shuzhi.js）
 * ============================================================
 * 「逻辑与推理」组新作：入门 5×5（默认档）、进阶 8×8、挑战 10×10。
 * 规则：盘外数字是每行/每列里连续黑格段的长度（按顺序排列），
 * 涂黑所有格让行列提示全部满足。
 *
 * 出题底线（唯一解校验，与数独/算独同源）：随机生成图案 → 算行列提示 →
 * 用按行候选回溯的解计数器验证「解恰好一个」，不唯一就重出——绝不发出
 * 多解题。所以页面敢承诺：卡住不是题目的错。
 *
 * 交互口径：点一下涂黑、再点标记 X（确定此处不涂）、再点清空——三态循环。
 * 行/列提示在「该行/列当前涂黑段与提示一致」时自动变淡（is-done）。
 * 涂错不即时判错（推理乐趣所在）；「查一查」找第一条矛盾讲清楚：
 * 这行提示要 [2,1]，你涂出了 [1,1,1]——多了或少了哪个段，段长差在哪。
 *
 * 成绩口径：不计时，只按档位记完成局数，全部存本机 localStorage。
 *
 * 冒烟钩子：window.STShuzhi 暴露 lineClue / lineOptions / solveCount /
 * genPuzzle / explainLine 与 state()（{n, puzzle, clues, grid}）。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-shuzhi';
  var RECORD_KEY = 'study-shuzhi-record';

  /* ============ 引擎：纯函数 ============ */

  // 一行 0/1 数组的段长提示：[0,1,1,0,1] → [2,1]；全空 → []
  function lineClue(cells) {
    var out = [], run = 0;
    for (var i = 0; i < cells.length; i++) {
      if (cells[i]) run++;
      else if (run) { out.push(run); run = 0; }
    }
    if (run) out.push(run);
    return out;
  }

  function eqArr(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
    return true;
  }

  // 一行所有满足提示的 0/1 排列（n ≤ 10 直接 2^n 枚举过滤，够快）
  function lineOptions(clue, n) {
    var out = [];
    var total = 1 << n;
    for (var mask = 0; mask < total; mask++) {
      var cells = new Array(n);
      for (var b = 0; b < n; b++) cells[b] = (mask >> (n - 1 - b)) & 1;
      if (eqArr(lineClue(cells), clue)) out.push(cells);
    }
    return out;
  }

  // 解计数（按行候选 + 列候选前缀过滤，上限 limit 默认 2）——唯一解校验的判据。
  // 每放一行，把每列的候选里与已放前缀矛盾的滤掉；某列候选清空即剪枝。
  // 走满 n 行时每列序列必与某个候选完全一致，无需终检。
  function solveCount(n, clues, limit) {
    var lim = limit || 2;
    var rowOpts = [];
    for (var r = 0; r < n; r++) rowOpts.push(lineOptions(clues.rows[r], n));
    var colOpts0 = [];
    for (var c = 0; c < n; c++) colOpts0.push(lineOptions(clues.cols[c], n));
    var colFilter = colOpts0;
    var count = 0;
    (function bt(r) {
      if (count >= lim) return;
      if (r === n) { count++; return; }
      var opts = rowOpts[r];
      for (var oi = 0; oi < opts.length; oi++) {
        var opt = opts[oi];
        var newFilters = new Array(n);
        var ok = true;
        for (var c = 0; c < n; c++) {
          var cur = colFilter[c];
          var kept = [];
          for (var k = 0; k < cur.length; k++) {
            if (cur[k][r] === opt[c]) kept.push(cur[k]);
          }
          if (!kept.length) { ok = false; break; }
          newFilters[c] = kept;
        }
        if (ok) {
          var saved = colFilter;
          colFilter = newFilters;
          bt(r + 1);
          colFilter = saved;
        }
        if (count >= lim) return;
      }
    })(0);
    return count;
  }

  function cluesFrom(puzzle, n) {
    var rows = [], cols = [];
    for (var r = 0; r < n; r++) {
      var row = [];
      for (var c = 0; c < n; c++) row.push(puzzle[r * n + c]);
      rows.push(lineClue(row));
    }
    for (var cc = 0; cc < n; cc++) {
      var col = [];
      for (var rr = 0; rr < n; rr++) col.push(puzzle[rr * n + cc]);
      cols.push(lineClue(col));
    }
    return { rows: rows, cols: cols };
  }

  // 一整局：随机图案起步 → 扰动爬山（翻转 1 格，解数下降或平移则接受）→
  // 收敛到唯一解。随机大盘几乎不天然唯一，纯重试不可行；扰动搜索用解数当
  // 梯度（limit=6 计数），几分钟内可收敛。全黑盘（行 [n] 列 [n]）唯一，作兜底。
  function genPuzzle(n) {
    for (var round = 0; round < 4; round++) {
      var pz = new Array(n * n);
      for (var i = 0; i < pz.length; i++) pz[i] = Math.random() < 0.5 ? 1 : 0;
      var count = solveCount(n, cluesFrom(pz, n), 6);
      var guard = 0;
      while (count > 1 && guard++ < 300) {
        var idx = Math.floor(Math.random() * pz.length);
        pz[idx] ^= 1;
        var nc = solveCount(n, cluesFrom(pz, n), 6);
        if (nc < count || (nc === count && Math.random() < 0.25)) {
          count = nc;   // 接受：解数下降，或平移防卡
        } else {
          pz[idx] ^= 1; // 回滚
        }
      }
      if (count === 1) {
        var clues = cluesFrom(pz, n);
        return { n: n, puzzle: pz.slice(), clues: clues };
      }
    }
    var black = new Array(n * n).fill(1);
    return { n: n, puzzle: black, clues: cluesFrom(black, n) };
  }

  // 查一查：找第一条矛盾的行/列，讲段长账本
  function explainLine(n, grid, clues) {
    var filled = grid.map(function (v) { return v === 1 ? 1 : 0; });
    for (var r = 0; r < n; r++) {
      var row = [];
      for (var c = 0; c < n; c++) row.push(filled[r * n + c]);
      var have = lineClue(row);
      if (!eqArr(have, clues.rows[r]) && row.indexOf(1) >= 0) {
        return '第 ' + (r + 1) + ' 行不对：提示要 [' + clues.rows[r].join('、') + ']，'
          + '你涂出了 [' + (have.join('、') || '没有') + ']。'
          + '段数不一样就数段——多涂的段要拆开补 X，少涂的段看看还能落在哪。';
      }
    }
    for (var c = 0; c < n; c++) {
      var col = [];
      for (var rr = 0; rr < n; rr++) col.push(filled[rr * n + c]);
      var haveC = lineClue(col);
      if (!eqArr(haveC, clues.cols[c]) && col.indexOf(1) >= 0) {
        return '第 ' + (c + 1) + ' 列不对：提示要 [' + clues.cols[c].join('、') + ']，'
          + '你涂出了 [' + (haveC.join('、') || '没有') + ']。列的道理和行一样：段数、段长、顺序都要对上。';
      }
    }
    return null;   // 没有矛盾
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { n: 5, name: '入门 5×5', hint: '默认档 · 先练段感' },
    { n: 8, name: '进阶 8×8', hint: '段数更多' },
    { n: 10, name: '挑战 10×10', hint: '双位提示出现' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    n: 5, puzzle: null, clues: null, grid: [],
    ended: false, soundOn: true, resetArm: false, resetTimer: null
  };

  function renderLevels() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(lv.n));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.n === lv.n) return;
          newGame(lv.n);
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevels() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.n;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function fmtClue(arr) {
    return arr.length ? arr.join(' ') : '0';
  }

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-nz-cols', String(S.n + 1));
    // 第一行：列提示（占位格 + n 个提示格）
    board.appendChild(el('div', 'st-nz-corner'));
    for (var c = 0; c < S.n; c++) {
      var colHead = el('div', 'st-nz-colhead', fmtClue(S.clues.cols[c]));
      colHead.setAttribute('data-col', String(c));
      board.appendChild(colHead);
    }
    for (var r = 0; r < S.n; r++) {
      var rowHead = el('div', 'st-nz-rowhead', fmtClue(S.clues.rows[r]));
      rowHead.setAttribute('data-row', String(r));
      board.appendChild(rowHead);
      for (var c2 = 0; c2 < S.n; c2++) {
        (function (idx) {
          var b = el('button', 'st-nz-cell');
          b.type = 'button';
          b.setAttribute('data-idx', String(idx));
          b.addEventListener('click', function () { onCell(idx); });
          board.appendChild(b);
        })(r * S.n + c2);
      }
    }
    renderCells();
  }

  function renderCells() {
    var cells = ui.board.querySelectorAll('.st-nz-cell');
    for (var i = 0; i < cells.length; i++) {
      var node = cells[i];
      var idx = Number(node.getAttribute('data-idx'));
      node.classList.toggle('is-on', S.grid[idx] === 1);
      node.classList.toggle('is-mark', S.grid[idx] === 2);
      node.setAttribute('aria-label', '第 ' + (Math.floor(idx / S.n) + 1) + ' 行第 ' + (idx % S.n + 1) + ' 列，'
        + (S.grid[idx] === 1 ? '已涂黑' : S.grid[idx] === 2 ? '标记 X' : '空'));
    }
    // 提示划淡：该行/列当前涂黑段与提示一致
    var filled = S.grid.map(function (v) { return v === 1 ? 1 : 0; });
    for (var r = 0; r < S.n; r++) {
      var row = [];
      for (var c = 0; c < S.n; c++) row.push(filled[r * S.n + c]);
      var rh = ui.board.querySelector('[data-row="' + r + '"]');
      if (rh) rh.classList.toggle('is-done', eqArr(lineClue(row), S.clues.rows[r]));
    }
    for (var c2 = 0; c2 < S.n; c2++) {
      var col = [];
      for (var rr = 0; rr < S.n; rr++) col.push(filled[rr * S.n + c2]);
      var ch = ui.board.querySelector('[data-col="' + c2 + '"]');
      if (ch) ch.classList.toggle('is-done', eqArr(lineClue(col), S.clues.cols[c2]));
    }
    renderStatus();
  }

  function renderStatus() {
    if (S.ended) return;
    var on = 0;
    for (var i = 0; i < S.grid.length; i++) { if (S.grid[i] === 1) on++; }
    ui.status.className = 'st-status';
    ui.status.textContent = '入门 ' + S.n + '×' + S.n + ' · 已涂 ' + on + ' 格 · 目标 ' + S.puzzle.reduce(function (a, v) { return a + v; }, 0) + ' 格';
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    for (var i = 0; i < LEVELS.length; i++) {
      var lv = LEVELS[i];
      var r = rec['x' + lv.n];
      box.appendChild(recordStat(
        lv.n + '×' + lv.n + '完成',
        (r && r.wins ? r.wins + ' 局' : '还没通关过——涂满一次就记在这里')
      ));
    }
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  function onCell(idx) {
    if (S.ended) return;
    S.grid[idx] = (S.grid[idx] + 1) % 3;   // 空 → 黑 → X → 空
    sfx(S.grid[idx] === 1 ? 'ok' : 'sel');
    renderCells();
    if (isComplete()) {
      winGame();
      return;
    }
    var v = S.grid[idx];
    var r = Math.floor(idx / S.n) + 1, c = idx % S.n + 1;
    renderNote(v === 1 ? '涂黑了第 ' + r + ' 行第 ' + c + ' 列——看看它的行提示和列提示是不是能对上。'
      : v === 2 ? '标记了 X（确定不涂）——X 也能帮你排除：一行里 X 之外的格就是段的候选位置。'
      : '清掉了。用行列提示重新想想这格。', '');
  }

  function isComplete() {
    var filled = S.grid.map(function (v) { return v === 1 ? 1 : 0; });
    var cur = cluesFrom(filled, S.n);
    for (var i = 0; i < S.n; i++) {
      if (!eqArr(cur.rows[i], S.clues.rows[i])) return false;
      if (!eqArr(cur.cols[i], S.clues.cols[i])) return false;
    }
    return true;
  }

  function checkBoard() {
    if (S.ended) return;
    var msg = explainLine(S.n, S.grid, S.clues);
    if (msg) {
      sfx('bad');
      renderNote(msg, 'bad');
      return;
    }
    var on = 0;
    for (var i = 0; i < S.grid.length; i++) { if (S.grid[i] === 1) on++; }
    renderNote(on
      ? '涂出来的行列都和提示对得上——继续：找「提示只有一段且长度超过半行」的行列先确定中间必黑的格子。'
      : '还没开始涂。先看数字最大的行：比如提示「4」在 5 格行里，中间 4 格怎么放都至少有 3 格是黑的——先把这些必黑格涂上。', '');
  }

  function winGame() {
    S.ended = true;
    sfx('win');
    var rec = loadRecord();
    var key = 'x' + S.n;
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0 };
    cur.wins++;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.n + '×' + S.n + ' 的图案织出来了——行列提示全部满足。点「新开一局」再来一张。';
    renderNote('数织的推理三板斧：大段必黑（提示超过半行时中段一定黑）、边角定界（第一格是 X 则段从后面找）、X 排除（确定不涂的格帮你收紧段的位置）。每局都验证过唯一解。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置数织的本机成绩');
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, n: S.n, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.n === 5 || d.n === 8 || d.n === 10) S.n = d.n;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame(n) {
    var p = genPuzzle(n);
    S.n = n;
    S.puzzle = p.puzzle;
    S.clues = p.clues;
    S.grid = new Array(n * n).fill(0);
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncLevels();
    buildBoard();
    renderRecord();
    renderButtons();
    renderNote('「入门 ' + n + '×' + n + '」：行左、列上的数字是连续黑格段的长度（按顺序）。点一下涂黑、再点标 X、再点清空——把所有提示都对上就赢。', '');
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-nz-levels]');
    ui.board = root.querySelector('[data-st-nz-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.n); });
    ui.checkBtn.addEventListener('click', checkBoard);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.n);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STShuzhi = {
    lineClue: lineClue,
    lineOptions: lineOptions,
    solveCount: solveCount,
    cluesFrom: cluesFrom,
    genPuzzle: genPuzzle,
    explainLine: explainLine,
    state: function () { return { n: S.n, puzzle: S.puzzle, clues: S.clues, grid: S.grid }; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
